"use client";
import React from "react";
import { motion } from "framer-motion";
import styled from "@emotion/styled";
import {
  FiBarChart2,
  FiDollarSign,
  FiFileText,
  FiLock,
  FiUpload,
  FiShoppingBag,
  FiMapPin,
  FiPieChart,
} from "react-icons/fi";
import Header from "../home/Header";
import Footer from "../home/Footer"

// Styled Components
const ScrollableWrapper = styled.div`
  height: 100vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }

  scrollbar-width: none;
  -ms-overflow-style: none;
`;
const PageContainer = styled.div`
  max-width: 100%;
  margin: 0 auto;
  padding: 0;
  background: #f9f9ff;
`;

const PageHeader = styled(motion.section)`
  text-align: center;
  margin-bottom: 5rem;
`;

const Title = styled.h1`
  font-size: 3.2rem;
  margin-bottom: 1.5rem;
  color: #1a1a2e;
  line-height: 1.2;
  font-weight: 800;
  position: relative;
  display: inline-block;

  &::after {
    content: "";
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 120px;
    height: 4px;
    background: linear-gradient(90deg, #635bff 0%, #8b85ff 100%);
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.3rem;
  color: #4a5568;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.7;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 2.5rem;
  margin-top: 4rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 3rem 2.5rem;
  box-shadow: 0 15px 30px -10px rgba(99, 91, 255, 0.1);
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.1);
  border: none;
  position: relative;
  overflow: hidden;
  z-index: 1;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      135deg,
      rgba(99, 91, 255, 0.03) 0%,
      rgba(99, 91, 255, 0.01) 100%
    );
    z-index: -1;
  }
`;

const FeatureIconWrapper = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: linear-gradient(135deg, #635bff 0%, #8b85ff 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
  box-shadow: 0 10px 20px -5px rgba(99, 91, 255, 0.3);
`;

const FeatureIcon = styled.div`
  font-size: 2.5rem;
  color: white;
`;

const FeatureTitle = styled.h3`
  font-size: 1.6rem;
  margin-bottom: 1.2rem;
  color: #1a1a2e;
  font-weight: 700;
`;

const ShortDescription = styled.p`
  color: #4a5568;
  margin-bottom: 2rem;
  font-size: 1.15rem;
  line-height: 1.7;
`;

const FeatureDetails = styled.div`
  border-top: 1px solid rgba(99, 91, 255, 0.1);
  padding-top: 2rem;
`;

const DetailTitle = styled.h4`
  font-size: 1.3rem;
  margin: 0 0 1.5rem 0;
  color: #1a1a2e;
  font-weight: 600;
  display: flex;
  align-items: center;

  &::before {
    content: "";
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #635bff;
    margin-right: 10px;
  }
`;

const BenefitItem = styled.li`
  font-size: 1.1rem;
  color: #4a5568;
  margin-bottom: 1rem;
  position: relative;
  padding-left: 30px;
  line-height: 1.6;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 8px;
    width: 18px;
    height: 18px;
    background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23635BFF'%3E%3Cpath d='M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z'/%3E%3C/svg%3E")
      no-repeat center center;
  }
`;

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 1.5rem 0;
`;

const LongDescription = styled.p`
  color: #4a5568;
  line-height: 1.8;
  margin-bottom: 2rem;
  font-size: 1.1rem;
`;

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    // transition: {
    //   duration: 0.8,
    //   ease: "easeOut",
    // },
  },
};

const staggerContainer = {
  visible: {
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.3,
    },
  },
};

const featureItem = {
  hidden: { y: 30, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    // transition: {
    //   duration: 0.6,
    //   ease: [0.175, 0.885, 0.32, 1.1],
    // },
  },
};

const features = [
  {
    icon: <FiUpload />,
    title: "Secure Excel/CSV Uploads",
    shortDesc:
      "Securely upload your financial reports from Flipkart and Meesho.",
    longDesc:
      "Our system allows you to manually upload sales, returns, and payment data downloaded from your seller accounts, ensuring your data remains completely under your control without requiring direct API access.",
    benefits: [
      "Bulk upload Excel/CSVs",
      "No direct seller API connection needed",
      "Support for massive files",
      "Background processing with Celery/Redis",
      "Data format validation",
    ],
  },
  {
    icon: <FiDollarSign />,
    title: "Accurate Profit & Loss (P&L)",
    shortDesc: "Calculate your exact net profit after all deductions and fees.",
    longDesc:
      "Upload your marketplace settlement and order reports to track net earnings after shipping fees, marketplace commissions, TDS deductions, and return charges with complete transparency.",
    benefits: [
      "Marketplace-wise P&L",
      "Daily & Monthly profit trends",
      "Accurate deduction accounting",
      "Net margin calculations",
      "Custom date range analysis",
    ],
  },
  {
    icon: <FiShoppingBag />,
    title: "Missing Return Tracking",
    shortDesc:
      "Track lost inventory and identify missing returns.",
    longDesc:
      "Stop losing money on unreturned inventory. Our system automatically matches returns against actual stock to fix null tracking issues and flag returns that were never received back.",
    benefits: [
      "Null stock value detection",
      "Lost inventory alerts",
      "Actual return vs expected return",
      "Return tracking ID validation",
      "Minimize RTO losses",
    ],
  },
  {
    icon: <FiPieChart />,
    title: "Cost Price Management",
    shortDesc: "Maintain and track your exact product acquisition costs.",
    longDesc:
      "Easily update and manage cost prices and purchase data for your entire inventory to ensure your profitability calculations are based on real acquisition costs, not just estimates.",
    benefits: [
      "Bulk cost price updates",
      "Purchase expense tracking",
      "Accurate COGS calculation",
      "Historical cost adjustments",
      "Per-SKU profitability",
    ],
  },
  {
    icon: <FiMapPin />,
    title: "Flipkart & Meesho Support",
    shortDesc: "Deep integration with top Indian e-commerce marketplaces.",
    longDesc:
      "Our system is uniquely tailored to parse, process, and analyze the specific formats of financial reports and settlement files provided by Flipkart and Meesho.",
    benefits: [
      "Native Flipkart report parsing",
      "Native Meesho report parsing",
      "Marketplace-specific deductions",
      "Reconciliation for both platforms",
      "Unified multi-channel view",
    ],
  },
  {
    icon: <FiBarChart2 />,
    title: "Automated Data Processing",
    shortDesc: "Process huge settlement files quickly in the background.",
    longDesc:
      "Our enterprise-grade backend powered by Celery and Redis processes your largest spreadsheet files securely in the background, so you can keep working without waiting.",
    benefits: [
      "Asynchronous background processing",
      "Handles 100k+ row Excel files",
      "Real-time progress indicators",
      "Reliable Redis task queuing",
      "Zero browser freezes",
    ],
  },
  {
    icon: <FiFileText />,
    title: "PDF & Visual Reporting",
    shortDesc: "Generate beautiful, comprehensive business reports.",
    longDesc:
      "Transform raw spreadsheet data into easy-to-understand visual charts and download detailed PDF reports for offline analysis or sharing with your accountant.",
    benefits: [
      "Visual data charts",
      "Exportable PDF reports",
      "Instant business snapshots",
      "Shareable financial summaries",
      "Easy-to-read metric dashboards",
    ],
  },
  {
    icon: <FiLock />,
    title: "Data Privacy & Security",
    shortDesc: "Your financial data is strictly used for your analytics.",
    longDesc:
      "We take privacy seriously. Files are securely processed solely for providing CRM functionalities. We do not sell or share your personal or financial data to any third parties.",
    benefits: [
      "Secure file handling",
      "Strict data privacy policy",
      "No third-party data sharing",
      "Manual upload control",
      "Isolated seller accounts",
    ],
  },
];

export default function FeaturesPage() {
  return (
    <ScrollableWrapper>
      <PageContainer>
        <Header />
        <PageHeader initial="hidden" animate="visible" variants={fadeIn}>
          <Title>E-Commerce Accounting Superpowers</Title>
          <Subtitle>
            Automate your financial workflows with specialized tools built for
            Flipkart and Meesho sellers.{" "}
            <span style={{ color: "#635bff", fontWeight: 600 }}>
              #EcomSimplified
            </span>
          </Subtitle>
        </PageHeader>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
          <FeaturesGrid>
            {features.map((feature, index) => (
              <FeatureCard
                key={index}
                variants={featureItem}
                whileHover={{
                  y: -10,
                  boxShadow: "0 20px 40px -10px rgba(99, 91, 255, 0.2)",
                }}
              >
                <FeatureIconWrapper>
                  <FeatureIcon>{feature.icon}</FeatureIcon>
                </FeatureIconWrapper>

                <FeatureTitle>{feature.title}</FeatureTitle>
                <ShortDescription>{feature.shortDesc}</ShortDescription>

                <FeatureDetails>
                  <DetailTitle>Key Capabilities</DetailTitle>
                  <LongDescription>{feature.longDesc}</LongDescription>

                  <DetailTitle>What You Get</DetailTitle>
                  <BenefitsList>
                    {feature.benefits.map((benefit, i) => (
                      <BenefitItem key={i}>{benefit}</BenefitItem>
                    ))}
                  </BenefitsList>
                </FeatureDetails>
              </FeatureCard>
            ))}
          </FeaturesGrid>
        </motion.div>
      </PageContainer>
      <Footer />
    </ScrollableWrapper>
  );
}
